import { useQuery } from "@apollo/client";
import { Delete, Edit, InfoOutlined } from "@mui/icons-material";
import Add from "@mui/icons-material/Add";
import { Box, Button, Tooltip } from "@mui/material";
import { last, sortBy } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useState } from "react";

import {
  DeletePriceOverrideMutation,
  DeletePriceOverrideMutationVariables,
  GetTenantPriceDataQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, {
  RowAction,
  TableBox,
  TableColumns,
} from "~frontend/components/Table";
import {
  DELETE_PRICE_OVERRIDE,
  PRICE_DATA_QUERY,
} from "~frontend/graphql/priceOverrides";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

import { CreatePriceOverrideDialog } from "./CreatePriceOverrideDialog";
import { EditStartingPriceDialog } from "./EditStartingPriceDialog";
import { formatMillicentsAsDollars } from "./priceFormatting";

type TableRowData = {
  id: string;
  label: string;
  mmsMessagePriceInMillicents: number;
  smsSegmentPriceInMillicents: number;
  mmsUnregisteredMessagePriceInMillicents: number;
  smsUnregisteredSegmentPriceInMillicents: number;
  smsShortcodeSegmentPriceInMillicents: number;
  mmsShortcodeMessagePriceInMillicents: number;
};

function formatStartDate(startDateISO: string): string {
  return DateTime.fromISO(startDateISO).toLocaleString(DateTime.DATE_SHORT);
}

const PricingHeader = ({
  title,
  type,
}: {
  title: string;
  type: "SMS" | "MMS";
}) => (
  <FlexBox alignItems="center" gap={0.5}>
    <Box>{title}</Box>
    <Tooltip title={type === "SMS" ? "per segment" : "per message"}>
      <InfoOutlined sx={{ fontSize: 15 }} />
    </Tooltip>
  </FlexBox>
);

export function Pricing(): ReactElement {
  const isStaff = useCurrentUserIsStaff();
  const [editStartingPriceDialogOpen, setEditStartingPriceDialogOpen] =
    useState(false);
  const [createPriceOverrideDialogOpen, setCreatePriceOverrideDialogOpen] =
    useState(false);

  useBreadcrumbs([
    {
      label: "Pricing",
      path: "/organization/pricng",
    },
  ]);

  const { loading, error, data } =
    useQuery<GetTenantPriceDataQuery>(PRICE_DATA_QUERY);

  const [deletePriceOverride] = useMutation<
    DeletePriceOverrideMutation,
    DeletePriceOverrideMutationVariables
  >(DELETE_PRICE_OVERRIDE, {
    onErrorSnackbarMessage: "Error deleting price override",
  });

  const baseColumns: TableColumns<TableRowData> = [
    {
      key: "smsSegmentPriceInMillicents",
      renderValue: row =>
        `$${formatMillicentsAsDollars(row.smsSegmentPriceInMillicents)}`,
      title: <PricingHeader title="SMS Price" type="SMS" />,
    },
    {
      key: "mmsMessagePriceInMillicents",
      renderValue: row =>
        `$${formatMillicentsAsDollars(row.mmsMessagePriceInMillicents)}`,
      title: <PricingHeader title="MMS Price" type="MMS" />,
    },
    {
      key: "smsUnregisteredSegmentPriceInMillicents",
      renderValue: row =>
        `$${formatMillicentsAsDollars(
          row.smsUnregisteredSegmentPriceInMillicents
        )}`,
      title: <PricingHeader title="Unregistered SMS Price" type="SMS" />,
    },
    {
      key: "mmsUnregisteredMessagePriceInMillicents",
      renderValue: row =>
        `$${formatMillicentsAsDollars(
          row.mmsUnregisteredMessagePriceInMillicents
        )}`,
      title: <PricingHeader title="Unregistered MMS Price" type="MMS" />,
    },
    {
      key: "smsShortcodeSegmentPriceInMillicents",
      renderValue: row =>
        `$${formatMillicentsAsDollars(row.smsShortcodeSegmentPriceInMillicents)}`,
      title: <PricingHeader title="Shortcode SMS Price" type="SMS" />,
    },
    {
      key: "mmsShortcodeMessagePriceInMillicents",
      renderValue: row =>
        `$${formatMillicentsAsDollars(row.mmsShortcodeMessagePriceInMillicents)}`,
      title: <PricingHeader title="Shortcode MMS Price" type="MMS" />,
    },
  ];

  function getRowActions(row: TableRowData): RowAction<TableRowData>[] {
    if (row.id === "default") {
      return [
        {
          id: "edit",
          type: "button",
          icon: <Edit />,
          tooltipLabel: "Edit Starting Price",
          onClick: () => setEditStartingPriceDialogOpen(true),
        },
      ];
    } else {
      return [
        {
          id: "delete",
          type: "button",
          icon: <Delete />,
          tooltipLabel: "Delete",
          confirm: {
            title: "Delete Price Change",
            description: "Are you sure you want to delete this price change?",
          },
          async onClick() {
            await deletePriceOverride({ variables: { input: { id: row.id } } });
          },
        },
      ];
    }
  }

  return (
    <PageContainer>
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {({ getTenantPriceData }) => {
          const overrides = sortBy(
            getTenantPriceData.overrides,
            "startDate"
          ).reverse();
          const firstOverride = last(overrides);

          const rows: TableRowData[] = [
            ...overrides.map(override => ({
              id: override.id,
              label: `${formatStartDate(override.startDate)} Onwards`,
              mmsMessagePriceInMillicents: override.mmsMessagePriceInMillicents,
              smsSegmentPriceInMillicents: override.smsSegmentPriceInMillicents,
              mmsUnregisteredMessagePriceInMillicents:
                override.mmsUnregisteredMessagePriceInMillicents,
              smsUnregisteredSegmentPriceInMillicents:
                override.smsUnregisteredSegmentPriceInMillicents,
              smsShortcodeSegmentPriceInMillicents:
                override.smsShortcodeSegmentPriceInMillicents,
              mmsShortcodeMessagePriceInMillicents:
                override.mmsShortcodeMessagePriceInMillicents,
            })),
            {
              id: "default",
              label: firstOverride
                ? `Before ${formatStartDate(firstOverride.startDate)}`
                : "",
              mmsMessagePriceInMillicents:
                getTenantPriceData.startingPrice.mmsMessagePriceInMillicents,
              smsSegmentPriceInMillicents:
                getTenantPriceData.startingPrice.smsSegmentPriceInMillicents,
              mmsUnregisteredMessagePriceInMillicents:
                getTenantPriceData.startingPrice
                  .mmsUnregisteredMessagePriceInMillicents,
              smsUnregisteredSegmentPriceInMillicents:
                getTenantPriceData.startingPrice
                  .smsUnregisteredSegmentPriceInMillicents,
              smsShortcodeSegmentPriceInMillicents:
                getTenantPriceData.startingPrice
                  .smsShortcodeSegmentPriceInMillicents,
              mmsShortcodeMessagePriceInMillicents:
                getTenantPriceData.startingPrice
                  .mmsShortcodeMessagePriceInMillicents,
            },
          ];

          const columns: TableColumns<TableRowData> = [
            ...(firstOverride
              ? [
                  {
                    key: "startAt",
                    renderValue: (row: TableRowData) => row.label,
                    title: "Effective Date",
                  },
                ]
              : []),
            ...baseColumns,
          ];

          return (
            <>
              <EditStartingPriceDialog
                open={editStartingPriceDialogOpen}
                onClose={() => setEditStartingPriceDialogOpen(false)}
                initialValues={getTenantPriceData.startingPrice}
                defaults={getTenantPriceData.defaults}
              />
              <CreatePriceOverrideDialog
                open={createPriceOverrideDialogOpen}
                onClose={() => setCreatePriceOverrideDialogOpen(false)}
                defaults={getTenantPriceData.defaults}
              />
              <FlexBox justifyContent="space-between" sx={{ marginBottom: 2 }}>
                <PageHeader>Pricing</PageHeader>
                {isStaff && (
                  <Button
                    variant="contained"
                    onClick={() => setCreatePriceOverrideDialogOpen(true)}
                  >
                    <Add />
                    New Price Change
                  </Button>
                )}
              </FlexBox>
              <TableBox>
                <Table
                  rows={rows}
                  columns={columns}
                  noFooter
                  rowActions={isStaff ? getRowActions : undefined}
                />
              </TableBox>
            </>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
